import React, { useEffect, useState, useCallback, useMemo } from "react";
import { buildModifyPayloadForSlTp } from "@/lib/terminal/engine/shared/orders";
import { Check, X } from "lucide-react";
import { useOpenedTradesStore } from "@/vybe-trader-client/opened-trades/stores/useOpenedTradesStore";
import { OneClickTradingDialog } from "@/components/dialogs/OneClickTradingDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";
import { useChartSettingsStore } from "@/lib/chart-settings";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import {
  formatOrderType,
  getTradeClosePrice,
} from "@/vybe-trader-client/opened-trades";
import { useSettingsStore } from "@/lib/settings";
import { useInteractiveSlNew, useInteractiveTpNew } from "./hooks";
import {
  calculateDefaultSlPrice,
  calculateDefaultTpPrice,
} from "@/lib/chart-helpers/calculateSLTPValues";
import TradeOrderScreen from "@/components/trades-view/trade-order/TradeOrderScreen";
import {
  ChartTradeToolbar,
  chartToolbarBtn,
  chartToolbarBtnGhost,
  chartToolbarBtnOutline,
  chartToolbarDivider,
} from "@/components/chart/ChartTradeToolbar";
import { cn } from "@/lib/utils";

interface TradeOverlayNewProps {
  ticket: string | null;
  chartInstanceRef: React.MutableRefObject<any>;
  onClose: () => void;
  isDesktop?: boolean;
  /** One-shot: pre-enable SL/TP placement lines when opened from footer TP/SL cells. */
  placementHints?: { activateSl?: boolean; activateTp?: boolean } | null;
  onConsumePlacementHints?: () => void;
}

export const TradeOverlayNew = React.memo<TradeOverlayNewProps>(
  ({
    ticket,
    chartInstanceRef,
    onClose,
    isDesktop = false,
    placementHints = null,
    onConsumePlacementHints,
  }) => {
    const currentTrade = useOpenedTradesStore((state) =>
      ticket
        ? state.allTrades.find((t) => t.ticket.toString() === ticket)
        : null,
    );

    const [editableSlValue, setEditableSlValue] = useState<number>(0);
    const [editableTpValue, setEditableTpValue] = useState<number>(0);

    const slEnabled = editableSlValue > 0;
    const tpEnabled = editableTpValue > 0;

    const [showOneClickDialog, setShowOneClickDialog] = useState(false);
    const [showUpdateDialog, setShowUpdateDialog] = useState(false);

    const { client } = useVybeTraderClient();
    const { getSetting } = useSettingsStore();

    const allSubscribedSymbols = useSubscribedSymbolsStore(
      (state) => state.byCurrency,
    );

    const defaultLevelsParams = useMemo(
      () =>
        currentTrade
          ? {
              quote: allSubscribedSymbols.get(currentTrade.symbol)?.quote,
              digits: currentTrade.digits,
            }
          : {},
      [currentTrade, allSubscribedSymbols],
    );

    useEffect(() => {
      if (currentTrade) {
        setEditableSlValue(currentTrade.stopLoss || 0);
        setEditableTpValue(currentTrade.takeProfit || 0);
      } else {
        setEditableSlValue(0);
        setEditableTpValue(0);
      }
    }, [ticket]);

    useEffect(() => {
      if (!placementHints) {
        return;
      }
      if (!placementHints.activateSl && !placementHints.activateTp) {
        onConsumePlacementHints?.();
        return;
      }
      if (!currentTrade) {
        return;
      }

      const wantSl =
        placementHints.activateSl && !(currentTrade.stopLoss > 0);
      const wantTp =
        placementHints.activateTp && !(currentTrade.takeProfit > 0);

      if (wantSl) {
        setEditableSlValue(
          calculateDefaultSlPrice(currentTrade, defaultLevelsParams),
        );
      }
      if (wantTp) {
        setEditableTpValue(
          calculateDefaultTpPrice(currentTrade, defaultLevelsParams),
        );
      }

      onConsumePlacementHints?.();
    }, [ticket, placementHints, currentTrade?.ticket, defaultLevelsParams]);

    const autoSlTpModify = useChartSettingsStore(
      (state) => state.chartSettings.features.autoSlTpModify,
    );

    const isPipsVisible = useChartSettingsStore(
      (state) => state.chartSettings.features.isPipsVisible,
    );

    const getCurrentPrice = (tradeToClose: {
      symbol: string;
      orderType: number;
      openPrice: number;
      closePrice?: number;
    }) => {
      const subscribedSymbol = allSubscribedSymbols.get(tradeToClose.symbol);
      const quote = subscribedSymbol?.quote;
      return getTradeClosePrice(tradeToClose, quote);
    };

    const handleCloseTrade = async () => {
      const oneClickTrading = getSetting("oneClickTrading");

      if (!oneClickTrading) {
        setShowOneClickDialog(true);
        return;
      }

      await executeCloseTrade();
    };

    const executeCloseTrade = async () => {
      if (!client || !client.isConnected()) {
        console.error("Client not connected");
        return;
      }

      try {
        const closeData = {
          Ticket: currentTrade!.ticket,
          Symbol: currentTrade!.symbol,
          Price: getCurrentPrice(currentTrade!),
          Lots: currentTrade!.lots,
          OrderType: currentTrade!.orderType,
          Slippage: 100,
        };

        await client.closeOrder(closeData);
        onClose();
      } catch (error) {
        console.error("Failed to close trade:", error);
      }
    };

    const handleSLModify = async (_type: "SL", price: number) => {
      await executeModify("SL", price);
    };

    const handleTPModify = async (_type: "TP", price: number) => {
      await executeModify("TP", price);
    };

    const handleUpdateLevels = () => {
      setShowUpdateDialog(true);
    };

    const executeModify = useCallback(
      async (type: "SL" | "TP", price: number) => {
        if (!client || !client.isConnected()) {
          console.error("Client not connected");
          return;
        }

        const freshTrade = useOpenedTradesStore
          .getState()
          .allTrades.find((t) => t.ticket.toString() === ticket);

        if (!freshTrade) {
          console.error("Trade not found in store");
          return;
        }

        try {
          let finalSL: number;
          let finalTP: number;

          if (type === "SL") {
            finalSL = price;
            finalTP = freshTrade.takeProfit || 0;
          } else {
            finalSL = freshTrade.stopLoss || 0;
            finalTP = price;
          }

          // Built centrally so dropping a level to 0 sends the explicit Clear flag; without it the
          // engine leaves the old level in place and the line reappears (SR Global, 2026-09-21).
          const modifyData = buildModifyPayloadForSlTp({
            ticket: freshTrade,
            stopLoss: finalSL,
            takeProfit: finalTP,
            comment: `Auto-modified ${type} via MT6 Trading App`,
          });

          await client.modifyOrder(modifyData);
        } catch (error) {
          console.error(`Failed to modify ${type}:`, error);
        }
      },
      [client, ticket],
    );

    const handleSL = () => {
      if (slEnabled) {
        setEditableSlValue(0);
      } else {
        const newSlValue =
          currentTrade?.stopLoss && currentTrade.stopLoss > 0
            ? currentTrade.stopLoss
            : calculateDefaultSlPrice(currentTrade!, defaultLevelsParams);
        setEditableSlValue(newSlValue);
      }
    };

    const handleTP = () => {
      if (tpEnabled) {
        setEditableTpValue(0);
      } else {
        const newTpValue =
          currentTrade?.takeProfit && currentTrade.takeProfit > 0
            ? currentTrade.takeProfit
            : calculateDefaultTpPrice(currentTrade!, defaultLevelsParams);
        setEditableTpValue(newTpValue);
      }
    };

    const updateEditableSlValue = (newValue: number) => {
      setEditableSlValue(newValue);
    };

    const updateEditableTpValue = (newValue: number) => {
      setEditableTpValue(newValue);
    };

    const { cleanup: cleanupSL } = useInteractiveSlNew({
      chartInstanceRef,
      ticket,
      slEnabled,
      editableSlValue,
      currentSlValue: currentTrade?.stopLoss || 0,
      autoSlTpModify,
      onPriceUpdate: updateEditableSlValue,
      onModify: handleSLModify,
      tradeData: currentTrade,
      isPipsVisible,
    });

    const { cleanup: cleanupTP } = useInteractiveTpNew({
      chartInstanceRef,
      ticket,
      tpEnabled,
      editableTpValue,
      currentTpValue: currentTrade?.takeProfit || 0,
      autoSlTpModify,
      onPriceUpdate: updateEditableTpValue,
      onModify: handleTPModify,
      tradeData: currentTrade,
      isPipsVisible,
    });

    const hasSlChanged =
      Math.abs(editableSlValue - (currentTrade?.stopLoss || 0)) > 0.00001;
    const hasTpChanged =
      Math.abs(editableTpValue - (currentTrade?.takeProfit || 0)) > 0.00001;
    const noEditableChanges = !hasSlChanged && !hasTpChanged;

    const handleDismiss = () => {
      if (cleanupSL) cleanupSL();
      if (cleanupTP) cleanupTP();
      onClose();
    };

    if (!ticket || !currentTrade) return null;

    const tradeChipLabel = `#${currentTrade.ticket} ${formatOrderType(currentTrade.orderType)} ${currentTrade.lots.toFixed(2)}`;

    return (
      <>
        <ChartTradeToolbar>
          <span
            className={cn(
              "shrink truncate px-1 text-[10px] font-medium tabular-nums text-muted-foreground",
              isDesktop ? "max-w-[9rem]" : "max-w-[7rem]",
            )}
            title={tradeChipLabel}
          >
            {tradeChipLabel}
          </span>

          <div className={chartToolbarDivider} aria-hidden />

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleSL}
              className={cn(
                chartToolbarBtn,
                "px-2",
                slEnabled
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : chartToolbarBtnOutline,
              )}
              title={slEnabled ? "Remove stop loss" : "Add stop loss"}
            >
              SL
            </button>
            <button
              type="button"
              onClick={handleTP}
              className={cn(
                chartToolbarBtn,
                "px-2",
                tpEnabled
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : chartToolbarBtnOutline,
              )}
              title={tpEnabled ? "Remove take profit" : "Add take profit"}
            >
              TP
            </button>
          </div>

          <div className={chartToolbarDivider} aria-hidden />

          <div className="flex items-center gap-0.5">
            <button
              type="button"
              disabled={noEditableChanges}
              onClick={handleUpdateLevels}
              className={cn(
                chartToolbarBtn,
                isDesktop ? "w-6" : "min-h-11 min-w-11",
                noEditableChanges
                  ? "text-muted-foreground opacity-50"
                  : "bg-primary text-primary-foreground hover:bg-primary/90",
              )}
              title="Update trade levels"
            >
              <Check className="h-3 w-3" />
            </button>
            {/* Labelled, not just a red ✗: it used to sit beside the identical-looking dismiss ✗ and a
                trader editing TP/SL closed a live position by mistake (SR Global, 2026-09-08). */}
            <button
              type="button"
              onClick={handleCloseTrade}
              className={cn(
                chartToolbarBtn,
                "gap-1 px-2",
                !isDesktop && "min-h-11",
                "bg-red-600 text-white hover:bg-red-700",
              )}
              title="Close position"
              aria-label="Close position"
            >
              <X className="h-3 w-3" />
              <span className="text-[10px] font-semibold uppercase tracking-wide">
                Close
              </span>
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className={cn(
                chartToolbarBtnGhost,
                !isDesktop && "min-h-11 min-w-11",
              )}
              title="Cancel editing (keeps the position)"
              aria-label="Cancel editing"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </ChartTradeToolbar>

        <OneClickTradingDialog
          open={showOneClickDialog}
          onOpenChange={setShowOneClickDialog}
          onConfirm={executeCloseTrade}
        />

        <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
          <DialogContent
            className={`${
              isDesktop ? "max-w-md max-h-[70vh]" : "max-w-lg max-h-[80vh]"
            } p-4`}
          >
            <DialogHeader className="pb-2">
              <DialogTitle className="text-lg">Update Trade Levels</DialogTitle>
            </DialogHeader>
            <div
              className={`${
                isDesktop ? "max-h-[55vh]" : "max-h-[60vh]"
              } overflow-y-auto`}
            >
              <TradeOrderScreen
                // Remount per open (and per trade) so the SL/TP inputs are
                // re-seeded from the current trade instead of stale state
                // from the previous time the dialog was opened.
                key={`${currentTrade?.ticket ?? "none"}-${showUpdateDialog}`}
                symbol={currentTrade?.symbol}
                type="modify"
                tradeData={currentTrade}
                initialStopLoss={editableSlValue || currentTrade?.stopLoss}
                initialTakeProfit={editableTpValue || currentTrade?.takeProfit}
                // SL/TP toggled OFF on the chart for a position that has the level = the trader is
                // removing it; without this the dialog re-seeded the old level and it could never go.
                clearStopLoss={!slEnabled && (currentTrade?.stopLoss ?? 0) > 0}
                clearTakeProfit={!tpEnabled && (currentTrade?.takeProfit ?? 0) > 0}
                isDesktop={isDesktop}
                onBack={() => setShowUpdateDialog(false)}
              />
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

TradeOverlayNew.displayName = "TradeOverlayNew";
